import React, { useContext, useState } from 'react';
import { bool, func, number, object, string } from 'prop-types';
import { get } from 'lodash';

import { FormContext, FormContextProvider } from '@stormwater-software/lib-form';
import ProjectContext from '../../../contexts/ProjectContext';

import FindingsForm, { getInitialData } from './FindingsForm';
import PlaceFindingOnMapsModal from './PlaceFindingOnMapsModal';
import findingApi from '../../../utils/api/finding';
import projectApi from '../../../utils/api/project';
import { parseLatLng } from '../../../utils/geo';
import FormModal from '../../Shared/form/modal/FormModal';
import ConfirmationModal from '../../Shared/ConfirmationModal/ConfirmationModal';
import { toast } from 'react-toastify';
import ToastError from '../../Shared/ToastError/ToastError';
import { InspectionContext } from '../../../contexts/InspectionContext';
import TenantFeatureContext from '../../../contexts/TenantContext';
import { useRequireClosurePhotos } from '../../../hooks/closurePhotos';

function AddFindingFormModalUI({
  hideModal,
  onAddFinding,
  inspectionId,
  fixedAssetId,
}) {
  const formContext = useContext(FormContext);
  const projectContext = useContext(ProjectContext);
  const inspectionContext = useContext(InspectionContext);
  const featureContext = useContext(TenantFeatureContext);
  const [noMetadataImage, setNoMetadataImage] = useState([]);
  const [showMetadataWarning, setShowMetadataWarning] = useState(false);
  const [isEnabledPhotoMetadata, setIsEnabledPhotoMetadata] = useState(false);
  const [placement, setPlacement] = useState(null);
  const [outOfBoundsConfirm, setOutOfBoundsConfirm] = useState(false);
  const requireClosurePhotos = useRequireClosurePhotos(
    get(projectContext, 'project.id') ||
      get(inspectionContext, 'inspection.project_id'),
    get(inspectionContext, 'inspection.template_id') ||
      get(inspectionContext, 'template.id'),
  );

  const createFinding = async () => {
    const toastId = toast.loading('Creating Finding...');

    try {
      const finding = await findingApi.create(inspectionId, formContext.value);
      onAddFinding(finding);
      toast.update(toastId, {
        render: 'Finding Created',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      return finding;
    } catch (err) {
      console.log(err);
      
      toast.update(toastId, {
        render: (
          <ToastError
            message='Cannot create finding because of error: '
            error={inspectionContext.setSaveError(err)}
          />
        ),
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      inspectionContext.reloadInspection();

      return null;
    }
  };

  const createAndClose = async () => {
    await createFinding();
    hideModal();
  };

  async function handleSubmit() {
    const coords = parseLatLng(formContext.value.location);
    const projectId = get(projectContext, 'project.id');

    // Case 1: not a coordinate, or GIS Configuration is off for the tenant → just create.
    if (
      !coords ||
      !projectId ||
      !navigator.onLine ||
      !featureContext.tenantHasFeature('GIS Configuration')
    ) {
      return createAndClose();
    }

    let check;

    try {
      check = await projectApi.checkCoordinateOnSiteMaps(
        projectId,
        inspectionId,
        coords.lat,
        coords.lng,
      );
    } catch (err) {
      console.log(err);

      return createAndClose();
    }

    // Case 2: project has no georeferenced maps → just create.
    if (!check.hasCalibratedMaps) {
      return createAndClose();
    }

    // Case 3: coordinate is on a georeferenced map → create, then place.
    if (check.containingMaps.length) {
      const finding = await createFinding();

      if (finding) {
        setPlacement({
          projectId,
          finding,
          coords,
          maps: check.containingMaps,
        });
      } else {
        hideModal();
      }

      return;
    }

    // Case 4: georeferenced maps exist but the coordinate is on none of them.
    setOutOfBoundsConfirm(true);
  }

  const handleSubmitClickNoMetadata = async () => {
    const noMetadataImage = formContext.value.images.filter((image) => {
      return image.gpsData === null;
    });

    if (noMetadataImage.length > 0 && isEnabledPhotoMetadata) {
      setNoMetadataImage(noMetadataImage);
      setShowMetadataWarning(true);

      return Promise.resolve();
    } else {
      return await handleSubmit();
    }
  };

  return (
    <>
      <FormModal
        modalProps={{
          title: 'Add a Finding',
          submitBtnText: 'Save',
          classes: 'add-finding-form',
          style: {},
        }}
        onCancel={hideModal}
        onSubmit={handleSubmitClickNoMetadata}
        dismissOnEsc={false}
      >
        <FindingsForm
          onChange={formContext.set}
          value={formContext.value}
          templateName={get(projectContext, 'template.name')}
          defaultObservation={get(
            projectContext,
            'project.client.default_finding_observation',
            '',
          )}
          inspectionId={inspectionId}
          templateID={get(inspectionContext, 'template.id')}
          isTemplateEnforcementEnabled={
            inspectionContext.inspectionLoaded
              ? get(inspectionContext, 'template.is_enforcement_enabled', false)
              : false
          }
          modal={true}
          setIsEnabledPhotoMetadata={setIsEnabledPhotoMetadata}
          noMetadataImage={noMetadataImage}
          showMetadataWarning={showMetadataWarning}
          setShowMetadataWarning={setShowMetadataWarning}
          fromFindingAdd={true}
          requireClosurePhotos={requireClosurePhotos}
          fixedAssetId={fixedAssetId}
        />
      </FormModal>

      <ConfirmationModal
        show={outOfBoundsConfirm}
        title='Location not on a site map'
        subTitle='The coordinates you entered are not within any georeferenced site map. Create the finding anyway, or cancel to change the location.'
        buttonText='Create Finding'
        handleConfirm={() => {
          setOutOfBoundsConfirm(false);
          createAndClose();
        }}
        handleClose={() => setOutOfBoundsConfirm(false)}
      />

      {placement && (
        <PlaceFindingOnMapsModal
          {...placement}
          onClose={() => {
            setPlacement(null);
            hideModal();
          }}
        />
      )}
    </>
  );
}

function AddFindingModal(props) {
  if (!props.show) {
    return null;
  }

  return (
    <FormContextProvider
      initialValue={{
        ...getInitialData(props.defaultDate),
        ...(props.fixedAssetId ? { project_asset_id: props.fixedAssetId } : {}),
        ...(props.defaultLocation ? { location: props.defaultLocation } : {}),
      }}
    >
      <AddFindingFormModalUI
        show={props.show}
        hideModal={props.hideModal}
        onAddFinding={props.onAddFinding}
        inspectionId={props.inspectionId}
        fixedAssetId={props.fixedAssetId}
      />
    </FormContextProvider>
  );
}

AddFindingModal.propTypes = {
  show: bool.isRequired,
  hideModal: func.isRequired,
  onAddFinding: func.isRequired,
  inspectionId: number.isRequired,
  defaultDate: object.isRequired,
  /** When set, the finding is pre-linked to this asset and the field is locked. */
  fixedAssetId: number,
  defaultLocation: string,
};

export default AddFindingModal;
